"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Sparkles, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityWindows = [{
  id: "peak",
  period: "October – February",
  name: "Peak Winter & Heritage Season",
  badge: "High ADR & Comp-Set Surge",
  icon: Sparkles,
  description: "Inflow of international leisure travelers, heritage enthusiasts in White Town, and year-end festival visitors driving maximum willingness to pay.",
  strategy: "Strict MLOS (2-3 nights), dynamic rate lifting on Fridays/Saturdays, and advance non-refundable rate fencing."
}, {
  id: "shoulder",
  period: "July – September",
  name: "Monsoon & Cultural Travel",
  badge: "Mid-Range Yield Stability",
  icon: CloudRain,
  description: "Auroville spiritual retreats, wellness tourism, and coastal monsoon road trips from Chennai and Bengaluru creating steady weekend occupancy.",
  strategy: "Targeted direct booking packages with dining or wellness credits to bypass commission-heavy OTA discounters."
}, {
  id: "summer",
  period: "March – June",
  name: "Summer & Weekend Micro-Trips",
  badge: "Tactical Volume Pacing",
  icon: Sun,
  description: "Lower weekday leisure volumes contrasted with intense 48-hour weekend road-trip compression from major southern metros.",
  strategy: "Intraday rate elasticity, mid-week workation fencing, and real-time comp-set undercutting alerts."
}];
const challenges = [{
  id: 1,
  title: "Weekend Compression & Weekday Dips",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp 48-hour Friday-to-Sunday demand spikes against quiet mid-week occupancy. We deploy algorithmic minimum length-of-stay controls and pace-based pricing so your property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & weekend shoulder restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com across White Town, Heritage Town, and ECR coastal properties to defend your RevPAR ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across Google Hotels and drive direct guest conversions through high-intent perks.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking engine optimization"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapepondicherry" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Pondicherry
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Pondicherry presents a distinct micro-market characterized by intense weekend road-trip compression from Chennai and Bengaluru, French Quarter heritage boutique exclusivity, and pronounced seasonal shifts. Sunil Singh delivers disciplined revenue management frameworks specifically calibrated for French Quarter villas, luxury beach resorts, and experiential boutique stays.
          </p>
        </div>

        {/* Seasonality Block */}
        <div className="mb-16 md:mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-4 border-b border-border/70 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary mb-1.5">
                <TrendingUp className="size-4" />
                <span>Micro-Market Cycle</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Pondicherry Seasonality & Yield Windows
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Synchronizing rate fences, minimum stays, and channel allocation against local demand fluctuations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityWindows.map((season, index) => {
            const IconComponent = season.icon;
            return <div key={season.id} data-index={index} className="rounded-lg border border-border bg-card p-6 shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                        <IconComponent className="size-5 text-chart-2" />
                      </div>
                      <span className="text-xs font-mono font-medium text-muted-foreground px-2 py-1 rounded-md bg-muted">
                        {season.period}
                      </span>
                    </div>

                    <h4 className="font-serif text-lg font-bold text-foreground mb-1">
                      {season.name}
                    </h4>
                    <span className="inline-block text-xs font-semibold text-secondary mb-3">
                      {season.badge}
                    </span>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {season.description}
                    </p>
                  </div>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3 text-xs">
                    <p className="font-semibold text-foreground mb-1">Yield Blueprint</p>
                    <p className="text-muted-foreground">{season.strategy}</p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* 3-Column Card Grid: Core Strategic Challenges */}
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
            Strategic Pillars
          </span>
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1 mb-8">
            Overcoming Core Distribution Barriers
          </h3>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Pondicherry hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}